import Text from "common/Typography/Text";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import NotificationActionRow from "features/settings/Security/NotificationsSettings/NotificationActionRow";
import Col from "antd/es/col";
import Row from "antd/es/row";
import styled, { css, useTheme } from "styled-components";
import { ChevronRight } from "lucide-react";
import { ArrowRightOutlined } from "common/Icons/Icons";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import useNotificationsSettingsSection from "@shared/features/settings/hooks/useNotificationsSettingsSection";
import { NotificationsSettingsSectionProps } from "@shared/features/settings/types";
import Flex from "common/Boxes/Flex";
import Checkbox from "common/Checkbox/Checkbox";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

const SettingsSection = ({
  section,
  actionTypes,
  index,
  expanded,
  onExpandCollapseClick,
  onCheckboxClick,
  notificationsData,
  columnSpans,
}: NotificationsSettingsSectionProps) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { isNewLook } = useTheme();

  const {
    isExpanded,
    telegramCheckboxData,
    webCheckboxData,
    isLastSection,
    telegramToggleOn,
    webToggleOn,
  } = useNotificationsSettingsSection({
    index,
    section,
    actionTypes,
    expanded,
    notificationsData,
    onCheckboxClick,
  });

  return (
    <SectionWrapper key={index}>
      <TitleWrapper onClick={() => onExpandCollapseClick(section)}>
        <Col span={columnSpans?.[0]}>
          <Flex $justifyContent="flex-start">
            {isNewLook ? (
              <>
                <NewLookArrow
                  size={16}
                  strokeWidth={2}
                  $expanded={isExpanded}
                />
                <SectionName $isMobile={isMobile}>{section}</SectionName>
              </>
            ) : (
              <>
                <SectionName $isMobile={isMobile}>{section}</SectionName>
                <ExpandCollapseArrow $expanded={isExpanded} />
              </>
            )}
          </Flex>
        </Col>
        <StyledCol span={columnSpans?.[1]}>
          {!telegramCheckboxData.hideCheckbox && (
            <Checkbox
              indeterminate={telegramCheckboxData.indeterminate}
              checked={telegramCheckboxData.checkAll}
              onClick={(e) => {
                e.stopPropagation();
                telegramCheckboxData.onClick();
              }}
              $useDisabledStyles={!telegramToggleOn}
            />
          )}
        </StyledCol>
        <StyledCol span={columnSpans?.[2]}>
          {!webCheckboxData.hideCheckbox && (
            <Checkbox
              indeterminate={webCheckboxData.indeterminate}
              checked={webCheckboxData.checkAll}
              onClick={(e) => {
                e.stopPropagation();
                webCheckboxData.onClick();
              }}
              $useDisabledStyles={!webToggleOn}
            />
          )}
        </StyledCol>
      </TitleWrapper>
      <ExpandCollapse expanded={isExpanded}>
        <ActionsWrapper>
          {actionTypes.map((actionTypeItem, key) => (
            <NotificationActionRow
              key={key}
              actionTypeItem={actionTypeItem}
              onCheckboxClick={onCheckboxClick}
              columnSpans={columnSpans}
              telegramToggleOn={telegramToggleOn}
              webToggleOn={webToggleOn}
            />
          ))}
        </ActionsWrapper>
      </ExpandCollapse>
      {!isLastSection && <StyledDivider />}
    </SectionWrapper>
  );
};

export default SettingsSection;

const SectionWrapper = styled.div`
  width: 100%;
`;
const SectionName = styled(Text)<{ $isMobile?: boolean }>`
  font-size: ${(p) =>
    p.$isMobile
      ? p.theme.typography.fontSizes.M
      : p.theme.typography.fontSizes.L};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        ${newLookType.panelTitle};
        color: ${getNewLookTokens(p.theme).ink};
      }
    `}
`;
const NewLookArrow = styled(ChevronRight)<{ $expanded?: boolean }>`
  flex: none;
  margin-right: 8px;
  color: ${(p) => p.theme.colors.secondaryText};
  transition: transform 0.2s ease;
  ${(p) =>
    p.$expanded &&
    css`
      transform: rotate(90deg);
    `}
`;
const ActionsWrapper = styled.div`
  display: grid;
  grid-gap: 12px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      grid-gap: 0;
      padding-bottom: 8px;
    `}
`;
const ExpandCollapseArrow = styled(ArrowRightOutlined)<{ $expanded?: boolean }>`
  width: 18px;
  height: 18px;
  margin-left: 8px;
  fill: ${(p) => p.theme.colors.primaryText};
  ${(p) =>
    p.$expanded &&
    `
  transform: rotate(90deg);
  `};
`;
const TitleWrapper = styled(Row)`
  cursor: pointer;
  margin-bottom: 12px;
  width: 100%;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-bottom: 0;
      min-height: 48px;
      padding: 0 20px;
      align-items: center;
      transition: background-color 120ms;
      &:hover {
        background: ${getNewLookTokens(p.theme).rowHover};
      }
    `}
`;
const StyledCol = styled(Col)`
  justify-content: flex-end;
  display: flex;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      justify-content: center;
    `}
`;
const StyledDivider = styled.div`
  border-bottom: ${(p) => p.theme.borders.light};
  margin: 20px 0;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      border-bottom: 1px solid ${getNewLookTokens(p.theme).hairline};
      margin: 0;
    `}
`;
